/**
 * Shared environment uniforms: one set of uniform objects referenced by the
 * voxel materials, the sky, clouds and water. The Sky owns the day/night
 * values and writes them every frame; everything else only reads.
 *
 * Colour convention: every colour here is LINEAR (not sRGB) scene-referred
 * radiance. The scene renders into an HDR target and PostFX tonemaps +
 * encodes to sRGB, so values above 1.0 are fine (sun disc, emissives).
 */

import * as THREE from 'three';

export interface EnvUniforms {
  [name: string]: THREE.IUniform;
  /** Seconds since start (animation clock). */
  uTime: THREE.IUniform<number>;
  /** Unit vector TOWARDS the dominant light body (sun by day, moon by night). */
  uSunDir: THREE.IUniform<THREE.Vector3>;
  /** Direct light radiance of that body; fades to black while it crosses the horizon. */
  uSunColor: THREE.IUniform<THREE.Color>;
  /** Unit vector towards the actual sun (below the horizon at night). */
  uTrueSunDir: THREE.IUniform<THREE.Vector3>;
  /** Ambient sky light colour, multiplied by a surface's skylight level. */
  uAmbientColor: THREE.IUniform<THREE.Color>;
  /** 0 (night) .. 1 (day) scalar. */
  uDayLight: THREE.IUniform<number>;
  /** Sky gradient colours, also used for water reflections. */
  uZenith: THREE.IUniform<THREE.Color>;
  uHorizon: THREE.IUniform<THREE.Color>;
  /** Distance fog (radial, world units from the camera). */
  uFogColor: THREE.IUniform<THREE.Color>;
  uFogNear: THREE.IUniform<number>;
  uFogFar: THREE.IUniform<number>;
  /** Extra fog tint added when looking towards the sun at sunrise/sunset. */
  uSunGlow: THREE.IUniform<THREE.Color>;
  /** Brightness option (vanilla gamma): 0 Moody .. 1 Bright. */
  uGamma: THREE.IUniform<number>;
  /** Dimension ambient light added everywhere regardless of skylight (Nether's
   *  ambient_light; black in the overworld). Linear radiance. */
  uDimAmbient: THREE.IUniform<THREE.Color>;
  /** Smoothed ground wetness 0..1 (render/wetness.ts): rain darkening, puddles, ripples. */
  uWetness: THREE.IUniform<number>;
}

export function createEnvUniforms(): EnvUniforms {
  return {
    uTime: { value: 0 },
    uSunDir: { value: new THREE.Vector3(0.3, 0.9, 0.3).normalize() },
    uSunColor: { value: new THREE.Color(3.0, 2.8, 2.5) },
    uTrueSunDir: { value: new THREE.Vector3(0.3, 0.9, 0.3).normalize() },
    uAmbientColor: { value: new THREE.Color(0.55, 0.7, 1.0) },
    uDayLight: { value: 1 },
    uZenith: { value: new THREE.Color(0.1, 0.3, 0.8) },
    uHorizon: { value: new THREE.Color(0.5, 0.7, 1.0) },
    uFogColor: { value: new THREE.Color(0.5, 0.7, 1.0) },
    uFogNear: { value: 100 },
    uFogFar: { value: 160 },
    uSunGlow: { value: new THREE.Color(0, 0, 0) },
    uGamma: { value: 0.5 },
    uDimAmbient: { value: new THREE.Color(0, 0, 0) },
    uWetness: { value: 0 },
  };
}
